/**
 * AI Settings Tab
 * User-level AI configuration: memory, assistant channels, confirmations,
 * and optional user-owned API keys.
 */

import { SettingHeader } from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { GraphQLService } from '@affine/core/modules/cloud/services/graphql';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import {
  FEATURE_KEY_ASSISTANT_TELEGRAM,
  FEATURE_KEY_USER_AI_KEYS,
  useFeatureGate,
} from '../../../../../modules/streamlain/hooks/use-feature-gate';
import { AiProviderPanel } from '../../account-setting/ai-provider-panel';
import { SettingsOfflineHeaderAction } from '../../shared/settings-offline-status';
import { AIConfirmationsSettings } from '../ai-confirmations';
import { AIPersonalizationSettings } from '../ai-personalization';

export const AISettings = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const { isFeatureAllowed } = useFeatureGate();
  const graphqlService = useService(GraphQLService);
  const workspace = useService(WorkspaceService).workspace;
  const hasUserAiKeys = isFeatureAllowed(FEATURE_KEY_USER_AI_KEYS);
  const personalMemoryAllowed = isFeatureAllowed('ai-personal-memory');
  const assistantTelegramAllowed = isFeatureAllowed(
    FEATURE_KEY_ASSISTANT_TELEGRAM
  );
  const [personalMemoryEnabled, setPersonalMemoryEnabled] = useState(false);
  const [assistantChannelAllowed, setAssistantChannelAllowed] = useState(false);

  useEffect(() => {
    if (!backendAvailable) {
      setPersonalMemoryEnabled(false);
      return;
    }
    if (!personalMemoryAllowed) {
      setPersonalMemoryEnabled(false);
      return;
    }

    graphqlService
      .gql({
        query: {
          id: 'workspaceMemoryStatusQuery',
          query: `query workspaceMemoryStatus($id: String!) {
            workspace(id: $id) { enablePersonalMemory }
          }`,
          operationName: 'workspaceMemoryStatus',
          definitionName: 'workspace',
        } as any,
        variables: { id: workspace.id } as any,
      })
      .then((res: any) => {
        setPersonalMemoryEnabled(Boolean(res?.workspace?.enablePersonalMemory));
      })
      .catch(error => {
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        setPersonalMemoryEnabled(false);
      });
  }, [backendAvailable, graphqlService, personalMemoryAllowed, workspace.id]);

  useEffect(() => {
    if (!backendAvailable) {
      setAssistantChannelAllowed(false);
      return;
    }
    if (!assistantTelegramAllowed) {
      setAssistantChannelAllowed(false);
      return;
    }
    workspaceApiFetch(
      `/api/workspace/${workspace.id}/assistant/me`,
      {
        credentials: 'include',
      },
      {
        workspaceId: workspace.id,
        category: 'settings',
        key: 'assistant-me',
        mode: 'background',
        priority: 'background',
      }
    )
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        setAssistantChannelAllowed(Boolean(data?.channels?.telegram?.enabled));
      })
      .catch(error => {
        if (isBackendUnavailableError(error)) {
          markBackendUnavailable(error);
        }
        setAssistantChannelAllowed(false);
      });
  }, [assistantTelegramAllowed, backendAvailable, workspace.id]);

  const showMemories = personalMemoryAllowed && personalMemoryEnabled;
  const showAssistantChannels = assistantChannelAllowed;
  const showPersonalization = showMemories || showAssistantChannels;

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.ai-settings.title']()}
        subtitle={t['com.streamlain.ai-settings.subtitle']()}
        action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
        data-testid="ai-settings-title"
      />
      {showPersonalization ? (
        <AIPersonalizationSettings
          showHeader={false}
          showMemories={showMemories}
          showTelegram={showAssistantChannels}
          showTemporaryGrants={showAssistantChannels}
        />
      ) : null}
      <section style={{ padding: '24px 0 8px' }}>
        <h2
          style={{
            margin: 0,
            color: 'var(--streamlain-text-primary-color)',
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '24px',
          }}
        >
          {t['com.streamlain.ai-confirmations.section-title']()}
        </h2>
        <p
          style={{
            margin: '4px 0 0',
            color: 'var(--streamlain-text-secondary-color)',
            fontSize: 13,
            lineHeight: '20px',
          }}
        >
          {t['com.streamlain.ai-confirmations.section-desc']()}
        </p>
      </section>
      <AIConfirmationsSettings showHeader={false} />
      {hasUserAiKeys ? <AiProviderPanel /> : null}
    </>
  );
};
